import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { FiArrowLeft } from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';
import { Box, Slide, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import useStyles from './styles';

import getValidationErrors, {
  IErrors,
} from '../../../../utils/getValidationErrors';

import BarberShopTitle from '../../../../components/BarberShopTitle';
import CustomForm from '../../../../components/CustomForm';
import { form, initialData, schema } from './form';
import userService from '../../../../services/userService';
import parseApiErrors from '../../../../utils/parseApiErrors';
import { NewPasswordRequestDTO } from '../../../../shared/interfaces/user-interfaces';
import LoadingButton from '../../../../components/LoadingButton';

interface IProps {
  userEmail: string;
}

const NewPasswordStep: React.FC<IProps> = ({ userEmail }) => {
  const classes = useStyles();
  const history = useHistory();

  const [errors, setErrors] = useState<IErrors>(initialData);

  const [data, setData] = useState<Omit<NewPasswordRequestDTO, 'email'>>(
    initialData,
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      try {
        await schema.validate(data, {
          abortEarly: false,
        });

        await userService.newPassword({
          token: data.token,
          email: userEmail,
          password: data.password,
          password2: data.password2,
        });

        history.push('/');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorsValidate = getValidationErrors(err);

          setErrors(errorsValidate);
          setLoading(false);
          return;
        }

        if (err.response.status === 400) {
          setErrors(parseApiErrors(err.response.data));
          setLoading(false);
          return;
        }

        toast.error('Ocorreu erro ao recuperar a senha. Tente novamente');
      } finally {
        setLoading(false);
      }
    },
    [data, history, userEmail],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        [e.target.name]: e.target.value,
      });
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    },
    [data, errors],
  );

  return (
    <Box display="flex" justifyContent="center" maxWidth="600px" margin="auto">
      <Slide in direction="left">
        <Box
          p={3}
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
          textAlign="center"
          className={classes.root}
        >
          <BarberShopTitle title="Agendfly" />
          <Typography variant="h5" className={classes.title}>
            Recuperação de Senha
          </Typography>
          <form onSubmit={handleSubmit} className={classes.form}>
            <CustomForm
              onChange={handleChange}
              data={data}
              form={form}
              errors={errors}
            />
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.submitButton}
              type="submit"
              size="large"
              fullWidth
              loading={loading}
            >
              Atualizar Senha
            </LoadingButton>
          </form>
          <Link to="/">
            <FiArrowLeft />
            Voltar para Login
          </Link>
        </Box>
      </Slide>
    </Box>
  );
};

export default NewPasswordStep;
